/**
 *
 * TODO: Looks like not very right way to do this.
 * TODO: We should have a single source of truth for the settings.
 * 
 *  Settings Normalizer Utility
 *
 * Consolidates building settings normalization logic.
 * Handles both camelCase (floorColor) and snake_case (floor_color) keys
 * that may come from different sources (forms, database, templates).
 */

import type { DoorConfig, UnitDoorsConfig, DoorType } from '@/lib/templates/types'

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/** Default colors when none specified */
export const DEFAULT_COLORS = {
  floor: '#808080',
  wall: '#e8e8e8',
} as const

/** Default elevation when not specified (feet) */
export const DEFAULT_ELEVATION_FT = 0

/** Default wall thickness in feet */
export const DEFAULT_WALL_THICKNESS_FT = 0.5

// ============================================================================
// TYPES
// ============================================================================

/** Raw settings from database (may have mixed key formats) */
export interface RawBuildingSettings {
  // Colors (both formats)
  floorColor?: string
  floor_color?: string
  wallColor?: string
  wall_color?: string
  // Dimensions
  wallThickness?: number
  wall_thickness?: number
  // Position
  position?: [number, number, number]
  // Doors
  doors?: DoorConfig[]
  // Outdoor area fields (border/floor)
  border_color?: string
  borderColor?: string
  border_style?: string
  borderStyle?: string
  border_width_ft?: number
  borderWidthFt?: number
  floor_opacity?: number
  floorOpacity?: number
}

/** Normalized settings with consistent snake_case keys */
export interface NormalizedBuildingSettings {
  floor_color: string
  wall_color: string
  wall_thickness_ft: number
  position?: [number, number, number]
  doors: DoorConfig[]
}

/** Raw type config from database */
export interface RawTypeConfig {
  template?: 'single_unit_community' | 'single_row' | 'double_row' | 'outdoor_rectangular' | 'standard_t_hangar' | 'nested_t_hangar'
  unit_count?: number
  wall_thickness_ft?: number
  calculated_unit_widths?: number[]
  row_a_overrides?: Array<{ index: number; width_ft?: number; doors?: UnitDoorsConfig }>
  row_b_overrides?: Array<{ index: number; width_ft?: number; doors?: UnitDoorsConfig }>
  // T-hangar specific fields (stored by generate.ts)
  unit_width_ft?: number
  wing_depth_ft?: number
  tail_depth_ft?: number
  tail_width_ft?: number
  unit_pitch_ft?: number
  // Legacy compat
  row_a_unit_count?: number
}

// ============================================================================
// NORMALIZATION FUNCTIONS
// ============================================================================

/**
 * Normalize floor color from settings
 * Handles both floorColor and floor_color keys
 *
 * @param settings - Raw settings object
 * @param fallback - Optional fallback color (defaults to DEFAULT_COLORS.floor)
 */
export function normalizeFloorColor(
  settings: RawBuildingSettings | null | undefined,
  fallback = DEFAULT_COLORS.floor
): string {
  if (!settings) return fallback
  return settings.floorColor || settings.floor_color || fallback
}

/**
 * Normalize wall color from settings
 * Handles both wallColor and wall_color keys
 *
 * @param settings - Raw settings object
 * @param fallback - Optional fallback color (defaults to DEFAULT_COLORS.wall)
 */
export function normalizeWallColor(
  settings: RawBuildingSettings | null | undefined,
  fallback = DEFAULT_COLORS.wall
): string {
  if (!settings) return fallback
  return settings.wallColor || settings.wall_color || fallback
}

/**
 * Normalize wall thickness from settings or type_config
 *
 * @param settings - Raw settings object
 * @param typeConfig - Optional type config with wall_thickness_ft
 * @param fallback - Optional fallback thickness (defaults to DEFAULT_WALL_THICKNESS_FT)
 */
export function normalizeWallThickness(
  settings: RawBuildingSettings | null | undefined,
  typeConfig?: RawTypeConfig | null,
  fallback = DEFAULT_WALL_THICKNESS_FT
): number {
  // Type config takes precedence
  if (typeConfig?.wall_thickness_ft != null) {
    return typeConfig.wall_thickness_ft
  }
  // Then settings
  if (settings) {
    if (settings.wall_thickness != null) return settings.wall_thickness
    if (settings.wallThickness != null) return settings.wallThickness
  }
  return fallback
}

/**
 * Normalize elevation from database altitude (stored in meters)
 *
 * @param altitudeMeters - Altitude in meters from database
 * @param fallback - Fallback elevation in feet (defaults to DEFAULT_ELEVATION_FT)
 */
export function normalizeElevation(
  altitudeMeters: number | null | undefined,
  fallback = DEFAULT_ELEVATION_FT
): number {
  const METERS_TO_FEET = 3.28084
  if (altitudeMeters == null) return fallback
  return altitudeMeters * METERS_TO_FEET
}

/**
 * Normalize bearing/rotation to 0-360 degrees
 *
 * @param rotation - Rotation in degrees (may be negative or > 360)
 */
export function normalizeBearing(rotation: number | null | undefined): number {
  const deg = rotation ?? 0
  return ((deg % 360) + 360) % 360
}

/**
 * Normalize all building settings to consistent format
 *
 * @param settings - Raw settings from database
 * @param typeConfig - Optional type config
 */
export function normalizeSettings(
  settings: RawBuildingSettings | null | undefined,
  typeConfig?: RawTypeConfig | null
): NormalizedBuildingSettings {
  return {
    floor_color: normalizeFloorColor(settings),
    wall_color: normalizeWallColor(settings),
    wall_thickness_ft: normalizeWallThickness(settings, typeConfig),
    position: settings?.position,
    doors: settings?.doors || [],
  }
}

// ============================================================================
// DOOR CONFIG HELPERS
// ============================================================================

/** Empty doors config for all 4 walls */
export function createEmptyDoorsConfig(): UnitDoorsConfig {
  return {
    front: [],
    back: [],
    left: [],
    right: [],
  }
}

/**
 * Convert flattened door configs (from database) to per-wall config
 *
 * @param doors - Array of door configs with wall property
 */
export function doorsArrayToConfig(doors: DoorConfig[] | null | undefined): UnitDoorsConfig {
  if (!doors || doors.length === 0) return {}

  const config: UnitDoorsConfig = {}
  for (const door of doors) {
    const wall = door.wall as keyof UnitDoorsConfig
    if (!config[wall]) {
      config[wall] = []
    }
    config[wall]!.push({
      offset_ft: door.offset_ft,
      width_ft: door.width_ft,
      height_ft: door.height_ft,
      type: door.type,
    })
  }
  return config
}

/**
 * Create default front door for a unit
 *
 * @param unitWidth - Interior width of the unit in feet
 * @param doorWidth - Door width in feet
 * @param doorHeight - Door height in feet
 * @param doorType - Type of door (bifold, sliding, etc.)
 */
export function createDefaultFrontDoor(
  unitWidth: number,
  doorWidth: number,
  doorHeight: number,
  doorType: DoorType = 'bifold'
): UnitDoorsConfig {
  return {
    front: [{
      offset_ft: (unitWidth - doorWidth) / 2, // Center the door
      width_ft: doorWidth,
      height_ft: doorHeight,
      type: doorType,
    }],
  }
}

/**
 * Create a centered door spec for a given wall width
 *
 * @param wallWidth - Width of the wall in feet
 * @param doorWidth - Door width in feet
 * @param doorHeight - Door height in feet
 * @param doorType - Type of door
 */
export function createCenteredDoorSpec(
  wallWidth: number,
  doorWidth: number,
  doorHeight: number,
  doorType: DoorType = 'bifold'
): { offset_ft: number; width_ft: number; height_ft: number; type: DoorType } {
  return {
    offset_ft: (wallWidth - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: doorHeight,
    type: doorType,
  }
}

/**
 * Merge door configs, combining doors on same walls
 *
 * @param configs - Array of door configs to merge
 */
export function mergeDoorConfigs(...configs: (UnitDoorsConfig | undefined)[]): UnitDoorsConfig {
  const result: UnitDoorsConfig = {}

  for (const config of configs) {
    if (!config) continue

    for (const wall of ['front', 'back', 'left', 'right'] as const) {
      if (config[wall] && config[wall]!.length > 0) {
        if (!result[wall]) {
          result[wall] = []
        }
        result[wall]!.push(...config[wall]!)
      }
    }
  }

  return result
}

/**
 * Check if a door config has any doors
 */
export function hasDoors(config: UnitDoorsConfig | null | undefined): boolean {
  if (!config) return false
  return Object.values(config).some(doors => doors && doors.length > 0)
}

/**
 * Count total doors in a config
 */
export function countDoors(config: UnitDoorsConfig | null | undefined): number {
  if (!config) return 0
  return Object.values(config).reduce(
    (sum, doors) => sum + (doors?.length || 0),
    0
  )
}
